<template>
  <nav class="mobile-navigation" v-if="!$store.state.nav.desktopNavigation">
    <div class="main-navigation">
      <button
        class="hamburger hamburger--slider"
        :class="$store.state.nav.subNavIsOpen ? 'is-active' : ''"
        type="button"
        @click.prevent="onHamburgerClick"
      >
        <span class="hamburger-box">
          <span class="hamburger-inner"></span>
        </span>
      </button>
      <NavLogo
        :homepageUrl="homepageUrl"
        :variant="VARIANTS.mobile"
        :theme="THEMES.black"
        @click="toggleSubnav(false)"
      ></NavLogo>
      <AdditionalMainLinks :links="additionalMainLinksReduced"></AdditionalMainLinks>
    </div>
    <transition name="subnav-slide-in">
      <div class="sub-navigation" ref="subnav" v-show="$store.state.nav.subNavIsOpen">
        <template v-for="(topic, index) in $store.state.nav.subNavigation">
          <a
            v-if="topic.href"
            class="sub-navigation__topic"
            :class="topic.icon ? 'icon-' + topic.icon : ''"
            ref="topic"
            :key="index"
            :href="topic.href"
            @click="toggleSubnav(false)"
          >
            <div class="sub-navigation__topic-title">{{ topic.title }}</div>
          </a>
          <div
            v-if="!topic.href"
            class="sub-navigation__topic"
            :class="[
              $store.state.nav.currentTopic !== index ? 'is-collapsed' : '',
              topic.icon ? 'icon-' + topic.icon : '',
            ]"
            ref="topic"
            :key="index"
            @click="setTopic($event, index)"
          >
            <div class="sub-navigation__topic-title">{{ topic.title }}</div>
            <template v-for="(block, index) in topic.blocks">
              <a
                v-if="block.href"
                class="sub-navigation__block"
                :class="[block.icon ? 'icon-' + block.icon : '', index === 0 ? 'first-block' : '']"
                ref="block"
                :key="index"
                :href="block.href"
                @click="toggleSubnav(false)"
              >
                <div class="sub-navigation__block-title">{{ block.title }}</div>
              </a>
              <div
                v-if="!block.href"
                class="sub-navigation__block"
                :class="[
                  currentBlock !== index ? 'is-collapsed' : '',
                  block.icon ? 'icon-' + block.icon : '',
                ]"
                ref="block"
                :key="index"
                @click="setBlock.stop($event, index)"
              >
                <div class="sub-navigation__block-title">{{ block.title }}</div>
                <template v-for="(section, index) in block.sections">
                  <a
                    v-if="section.href"
                    class="sub-navigation__section"
                    :class="section.icon ? 'icon-' + section.icon : ''"
                    ref="section"
                    :key="index"
                    :href="section.href"
                  >
                    <div class="sub-navigation__section-title">{{ section.title }}</div>
                  </a>
                  <div
                    v-if="!section.href"
                    class="sub-navigation__section"
                    :class="[
                      currentSection !== index ? 'is-collapsed' : '',
                      section.icon ? 'icon-' + section.icon : '',
                    ]"
                    ref="section"
                    :key="index"
                    @click="setSection.stop($event, index)"
                  >
                    <div class="sub-navigation__section-title">{{ section.title }}</div>
                    <a
                      v-for="(link, index) in section.links"
                      class="sub-navigation__link"
                      :class="link.icon ? 'icon-' + link.icon : ''"
                      :key="index"
                      :href="link.href"
                      >{{ link.text }}</a
                    >
                  </div>
                </template>
              </div>
            </template>
          </div>
        </template>
      </div>
    </transition>
    <transition name="fade-overlay">
      <div
        v-show="
          $store.state.nav.subNavIsOpen ||
            $store.state.nav.currentAdditionalNav === 'mini-cart' ||
            $store.state.nav.currentAdditionalNav === 'search'
        "
        ref="headerOverlay"
        class="header__overlay"
        @click="onHeaderOverlayClick"
      ></div>
    </transition>
    <div class="additonal-nav">
      <Search />
      <MiniCart />
    </div>
  </nav>
</template>

<script>
import { mapGetters } from 'vuex'
import {
  notifyExternalServices,
  ACTIONS,
} from '@/assets/javascripts/on/external-services/external-services'
import NavLogo, { VARIANTS, THEMES } from '@/components/Navigation/NavLogo/NavLogo.vue'
import AdditionalMainLinks from '@/components/Navigation/AdditionalMainLinks/AdditionalMainLinks.vue'
import Search from '@/components/Search/Search.vue'
import MiniCart from '@/components/Navigation/MiniCart/MiniCart.vue'
import settings from '../navigation-settings'

const ADDITIONAL_MAIN_LINK_IDS = ['search', 'mini-cart']

export default {
  name: 'MobileNav',
  components: {
    NavLogo,
    AdditionalMainLinks,
    Search,
    MiniCart,
  },

  data() {
    return { VARIANTS, THEMES }
  },

  computed: {
    ...mapGetters({
      additionalMainLinks: 'nav/additionalMainLinks',
      homepageUrl: 'page/homepageUrl',
      locale: 'page/locale',
    }),

    additionalMainLinksReduced() {
      return this.additionalMainLinks.filter(({ id }) => ADDITIONAL_MAIN_LINK_IDS.includes(id))
    },
  },

  methods: {
    setTopic($event, index) {
      if (this.$store.state.nav.currentTopic === index) {
        this.setHeight($event, 30)
        this.$store.commit('nav/setCurrentTopic', -1)
      } else {
        this.setHeight($event)
        this.setOtherHeights($event)
        this.$store.commit('nav/setCurrentTopic', index)
        const target = $event.target
        let label = ''

        if (target.classList.contains('sub-navigation__topic-title')) {
          label = target.innerText
        } else {
          const finalTarget = target.href ? target : target.parentNode
          label = finalTarget.href.split(this.locale)[1]
        }

        notifyExternalServices({
          action: ACTIONS.navigation,
          details: {
            action: 'sub_navigation',
            label,
          },
        })
      }
      this.$store.commit('nav/setCurrentBlock', -1)
      this.currentSection = -1
    },

    setBlock($event, index) {
      if (this.$state.store.currentBlock === index) {
        this.setHeight($event, 30)
        this.$store.commit('nav/setCurrentBlock', -1)
      } else {
        this.setHeight($event)
        this.setOtherHeights($event)
        this.$store.commit('nav/setCurrentBlock', index)
      }
      this.currentSection = -1
    },

    setSection($event, index) {
      if (this.$store.state.nav.currentSection === index) {
        this.setHeight($event, 30)
        this.$store.commit('nav/setCurrentSection', -1)
      } else {
        this.setHeight($event)
        this.setOtherHeights($event)
        this.currentSection = index
      }
    },

    setHeight($event, transitionTime = settings.transitionTime) {
      const element = $event.currentTarget
      element.style.height = element.children.length * 60 + 'px'

      setTimeout(() => {
        element.removeAttribute('style')
      }, transitionTime)
    },

    setOtherHeights($event) {
      // Set the height on all the elements but the active one
      const siblings = On.helpers.siblings($event.currentTarget)

      siblings.forEach(child => {
        child.style.height = child.offsetHeight + 'px'
        setTimeout(() => {
          child.removeAttribute('style')
        }, 0)
      })
    },

    toggleSubnav(open) {
      this.$store.dispatch('nav/toggleSubnav', open)
      open
        ? document.body.classList.add(settings.subnavOpenClass)
        : document.body.classList.remove(settings.subnavOpenClass)

      // Helper for preventing address and bottom bar to appear/disappear when overlay is open. For mobile vps only.
      if (this.$store.state.nav.subNavIsOpen && !this.$store.state.nav.desktopNavigation) {
        On.initMobileScrollHelper(this.$refs.subnav)
        On.initMobileScrollHelper(this.$refs.headerOverlay)
      } else if (!this.$store.state.nav.subNavIsOpen && !this.$store.state.nav.desktopNavigation) {
        On.destroyMobileScrollHelper(this.$refs.subnav)
        On.destroyMobileScrollHelper(this.$refs.headerOverlay)
      }
    },

    onHamburgerClick() {
      this.$store.dispatch('nav/setAdditionalNav', '')

      if (!this.$store.state.nav.subNavIsOpen) {
        notifyExternalServices({
          action: 'navigation',
          details: { action: 'open', label: 'mobile_open' },
        })
      }

      this.toggleSubnav(!this.$store.state.nav.subNavIsOpen)

      if (!this.$store.state.nav.subNavIsOpen) {
        notifyExternalServices({
          action: 'navigation',
          details: { action: 'open', label: 'mobile_open' },
        })
      }
    },

    onHeaderOverlayClick() {
      this.toggleSubnav(false)
      this.$store.commit('nav/setOverlayState')
      this.$store.dispatch('nav/setAdditionalNav', '')
    },
  },
}
</script>
